// Ultimate Card Game (UCG) - Audio Debug & Spontaneous Findings Overlay
//
// In-game development HUD (toggleable via Hotkey 'U' or window.UCG_DEBUG.toggleAudioLog())
// Logs recently played sounds in real time and provides a 1-click note-taking
// scratchpad for spontaneous findings during gameplay/playtesting.
//
// Notes are saved directly to `tools/sound-review/data/spontaneous-findings.json`
// via POST /api/sound-review/note (with localStorage fallback).

export class AudioDebugOverlay {
  constructor(game) {
    this.game = game;
    this.isOpen = false;
    this.log = [];
    this.maxEntries = 25;
    this.selectedEntry = null;
    this.container = null;
    this._boundOnKeyDown = this.onKeyDown.bind(this);
    this._boundOnAudioPlay = this.onAudioPlay.bind(this);
    this.init();
  }

  init() {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    window.addEventListener('keydown', this._boundOnKeyDown);
    if (this.game?.audio?.addLogListener) {
      this.game.audio.addLogListener(this._boundOnAudioPlay);
    }

    this._createDOM();
  }

  destroy() {
    window.removeEventListener('keydown', this._boundOnKeyDown);
    if (this.game?.audio?.removeLogListener) {
      this.game.audio.removeLogListener(this._boundOnAudioPlay);
    }
    if (this.container?.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }

  onAudioPlay(entry) {
    this.log.unshift({
      id: Math.random().toString(36).slice(2, 9),
      file: entry.file,
      timestamp: entry.timestamp || Date.now(),
      volume: entry.opts?.volume ?? 1,
      randomPitch: entry.opts?.randomPitch ?? false
    });
    if (this.log.length > this.maxEntries) {
      this.log.length = this.maxEntries;
    }
    if (this.isOpen) {
      this._renderLog();
    }
  }

  onKeyDown(e) {
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) {
      return;
    }
    if (e.key === 'u' || e.key === 'U') {
      this.toggle();
    } else if (e.key === 'Escape' && this.isOpen) {
      this.close();
    }
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.isOpen = true;
    if (this.container) {
      this.container.classList.add('active');
      this._renderLog();
    }
  }

  close() {
    this.isOpen = false;
    if (this.container) {
      this.container.classList.remove('active');
    }
  }

  _createDOM() {
    const el = document.createElement('div');
    el.id = 'audio-debug-overlay';
    el.className = 'audio-debug-overlay';
    el.innerHTML = `
      <style>
        .audio-debug-overlay {
          position: fixed;
          top: 12px;
          right: 12px;
          width: 380px;
          max-height: 86vh;
          background: rgba(16, 20, 26, 0.94);
          backdrop-filter: blur(10px);
          border: 1px solid #2a3240;
          border-radius: 8px;
          color: #e7ecf2;
          font-family: -apple-system, "Segoe UI", sans-serif;
          font-size: 12px;
          z-index: 999999;
          display: none;
          flex-direction: column;
          box-shadow: 0 16px 36px rgba(0,0,0,0.6);
        }
        .audio-debug-overlay.active { display: flex; }
        .ado-header {
          padding: 10px 14px;
          background: #171d25;
          border-bottom: 1px solid #2a3240;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-top-left-radius: 7px;
          border-top-right-radius: 7px;
        }
        .ado-header h3 { margin: 0; font-size: 13px; font-weight: 600; color: #4fd8a8; }
        .ado-close {
          background: transparent;
          border: none;
          color: #93a1b2;
          font-size: 16px;
          cursor: pointer;
        }
        .ado-close:hover { color: #fff; }
        .ado-feed {
          flex: 1;
          overflow-y: auto;
          max-height: 45vh;
          padding: 6px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .ado-feed::-webkit-scrollbar { width: 4px; }
        .ado-feed::-webkit-scrollbar-thumb { background: #2a3240; border-radius: 2px; }
        .ado-item {
          padding: 6px 8px;
          background: #1d2530;
          border: 1px solid #252d3a;
          border-radius: 4px;
          cursor: pointer;
          transition: background .12s, border-color .12s;
        }
        .ado-item:hover { background: #232c3a; border-color: #4fd8a8; }
        .ado-item.selected { border-color: #4fd8a8; background: #1c3a30; }
        .ado-item-top {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          margin-bottom: 2px;
        }
        .ado-file {
          font-family: monospace;
          font-size: 11.5px;
          font-weight: 600;
          color: #e7ecf2;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 250px;
        }
        .ado-time { font-size: 10px; color: #5f6c7d; }
        .ado-meta { font-size: 10px; color: #93a1b2; display: flex; gap: 8px; }
        .ado-scratchpad {
          padding: 12px;
          border-top: 1px solid #2a3240;
          background: #171d25;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .ado-input-row { display: flex; flex-direction: column; gap: 3px; }
        .ado-input-row label { font-size: 10.5px; color: #93a1b2; text-transform: uppercase; letter-spacing: 0.5px; }
        .ado-input {
          background: #10141a;
          border: 1px solid #2a3240;
          border-radius: 4px;
          color: #fff;
          font-family: inherit;
          font-size: 12px;
          padding: 6px 8px;
        }
        .ado-input:focus { outline: none; border-color: #4fd8a8; }
        .ado-actions { display: flex; gap: 8px; justify-content: flex-end; align-items: center; margin-top: 4px; }
        .ado-status { font-size: 11px; color: #4fd8a8; flex: 1; }
        .ado-btn {
          padding: 6px 14px;
          border-radius: 4px;
          font-weight: 600;
          font-size: 11.5px;
          cursor: pointer;
          border: 1px solid #2a3240;
          background: #1d2530;
          color: #e7ecf2;
        }
        .ado-btn-primary {
          background: #1c3a30;
          border-color: #4fd8a8;
          color: #4fd8a8;
        }
        .ado-btn-primary:hover { background: #4fd8a8; color: #10141a; }
      </style>
      <div class="ado-header">
        <h3>🎧 Audio Logger & Spontanfunde</h3>
        <button class="ado-close" id="ado-close">✕</button>
      </div>
      <div class="ado-feed" id="ado-feed">
        <div style="padding: 16px; text-align: center; color: #5f6c7d;">Noch keine Sounds abgespielt.</div>
      </div>
      <div class="ado-scratchpad">
        <div class="ado-input-row">
          <label>Selected Sound</label>
          <input class="ado-input" id="ado-selected-sound" readonly placeholder="Click a sound above…">
        </div>
        <div class="ado-input-row">
          <label>Issue / Note</label>
          <input class="ado-input" id="ado-note-text" placeholder="e.g. Too loud, sounds thin, plays twice…">
        </div>
        <div class="ado-input-row">
          <label>Suggestion / Finding (Optional)</label>
          <input class="ado-input" id="ado-suggested-file" placeholder="e.g. more/weapons/flame_thrower.m4a">
        </div>
        <div class="ado-actions">
          <span class="ado-status" id="ado-status"></span>
          <button class="ado-btn ado-btn-primary" id="ado-save-btn">Notiz festhalten</button>
        </div>
      </div>
    `;

    document.body.appendChild(el);
    this.container = el;

    el.querySelector('#ado-close')?.addEventListener('click', () => this.close());
    el.querySelector('#ado-save-btn')?.addEventListener('click', () => this._saveFinding());
  }

  _renderLog() {
    const feed = this.container?.querySelector('#ado-feed');
    if (!feed) return;

    if (!this.log.length) {
      feed.innerHTML = '<div style="padding: 16px; text-align: center; color: #5f6c7d;">Noch keine Sounds abgespielt.</div>';
      return;
    }

    const now = Date.now();
    feed.innerHTML = this.log.map((item) => {
      const isSel = this.selectedEntry?.id === item.id;
      const secAgo = Math.max(0, Math.round((now - item.timestamp) / 1000));
      const timeStr = secAgo < 60 ? `${secAgo}s her` : `${Math.round(secAgo / 60)}m her`;
      return `
        <div class="ado-item ${isSel ? 'selected' : ''}" data-id="${item.id}">
          <div class="ado-item-top">
            <span class="ado-file" title="${item.file}">${item.file}</span>
            <span class="ado-time">${timeStr}</span>
          </div>
          <div class="ado-meta">
            <span>Vol: ${Math.round(item.volume * 100)}%</span>
            ${item.randomPitch ? '<span>🎲 Pitch-Var</span>' : ''}
          </div>
        </div>
      `;
    }).join('');

    feed.querySelectorAll('.ado-item').forEach((itemEl) => {
      itemEl.addEventListener('click', () => {
        const id = itemEl.dataset.id;
        const entry = this.log.find((l) => l.id === id);
        if (entry) {
          this.selectedEntry = entry;
          const input = this.container.querySelector('#ado-selected-sound');
          if (input) input.value = entry.file;
          this._renderLog();
        }
      });
    });
  }

  async _saveFinding() {
    const noteInput = this.container.querySelector('#ado-note-text');
    const suggestInput = this.container.querySelector('#ado-suggested-file');
    const statusEl = this.container.querySelector('#ado-status');

    const note = noteInput?.value?.trim();
    if (!note) {
      if (statusEl) statusEl.textContent = 'Bitte Notiz eingeben!';
      return;
    }

    const finding = {
      soundFile: this.selectedEntry?.file || 'unbekannt / allgemeines Event',
      note,
      suggestedFile: suggestInput?.value?.trim() || null,
      timestamp: Date.now()
    };

    if (statusEl) statusEl.textContent = 'Speichere…';

    try {
      const res = await fetch('api/sound-review/note', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(finding)
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      if (statusEl) statusEl.textContent = '✓ Saved!';
      if (noteInput) noteInput.value = '';
      if (suggestInput) suggestInput.value = '';
      setTimeout(() => { if (statusEl) statusEl.textContent = ''; }, 3000);
    } catch (err) {
      // Fallback to localStorage
      try {
        const key = 'ucg_spontaneous_findings';
        const list = JSON.parse(localStorage.getItem(key) || '[]');
        list.unshift(finding);
        localStorage.setItem(key, JSON.stringify(list));
        if (statusEl) statusEl.textContent = '✓ Saved locally!';
      } catch {
        if (statusEl) statusEl.textContent = 'Error saving!';
      }
    }
  }
}
